/**
 * GraveForge Theme Configuration
 * Centralized Brand Colors & Design System
 *
 * Aligned with Graveyard (GraveMarket) and Launchpad (Gravemint) design systems.
 * Surface scale inspired by Graveyard's dark blue-gray palette.
 * Primary red matches Launchpad's brand.
 */

export const theme = {
  colors: {
    // Primary colors (Brand Red — matches Launchpad)
    primary: '#DC2626',
    primaryLight: '#EF4444',
    primaryDark: '#991B1B',
    primaryDeep: '#7f1d1d',
    secondary: '#14F195',      // Mint green accent (kept from original)

    // Accent colors
    accent: '#F87171',
    accentGlow: '#FCA5A5',

    // Surface scale (dark blue-grays — inspired by Graveyard)
    surface950: '#080b12',     // Page/body background
    surface900: '#0d1117',     // Darker surfaces
    surface850: '#12181f',     // Between surfaces
    surface800: '#161d2a',     // Card backgrounds
    surface750: '#1e2736',     // Subtle surface
    surface700: '#27303f',     // Borders, card outlines
    surface600: '#475569',     // Hover borders

    // Legacy background colors (kept for backward compat, prefer surface*)
    bgPrimary: '#000000',
    bgDark: '#0a0a0a',
    bgSecondary: '#1F2937',
    bgCard: 'rgba(13, 17, 23, 0.6)',     // surface900-based with transparency
    bgGlass: 'rgba(22, 29, 42, 0.7)',    // surface800-based glass
    bgGlassStrong: 'rgba(22, 29, 42, 0.85)', // Stronger glass for panels

    // Border colors — neutral by default, red only for accents (matches Graveyard/Gravemint)
    borderPrimary: 'rgba(39, 48, 63, 0.6)',      // surface700 based (neutral, not red)
    borderPrimaryHover: 'rgba(71, 85, 105, 0.8)', // surface600 based hover
    borderSubtle: 'rgba(255, 255, 255, 0.06)',   // Very subtle (matches Graveyard)
    borderMedium: 'rgba(255, 255, 255, 0.1)',    // Medium border
    borderAccent: 'rgba(220, 38, 38, 0.3)',      // Red accent border — use sparingly
    borderAccentHover: 'rgba(220, 38, 38, 0.5)', // Red accent hover

    // Primary alpha variants
    primaryAlpha5: 'rgba(220, 38, 38, 0.05)',
    primaryAlpha10: 'rgba(39, 48, 63, 0.4)',
    primaryAlpha15: 'rgba(39, 48, 63, 0.5)',
    primaryAlpha20: 'rgba(220, 38, 38, 0.2)',
    primaryAlpha30: 'rgba(220, 38, 38, 0.3)',
    primaryAlpha50: 'rgba(220, 38, 38, 0.5)',
    primaryAlpha80: 'rgba(220, 38, 38, 0.8)',

    // Text colors
    textPrimary: '#ffffff',
    textSecondary: '#94a3b8',  // Lighter gray (was #6B7280) — better contrast
    textMuted: '#64748b',      // Muted text (updated from dark gray)
    textDim: '#475569',        // Very dim text

    // Status colors
    success: '#10b981',
    warning: '#f59e0b',
    error: '#EF4444',
    info: '#3b82f6',
  },

  // Typography
  typography: {
    fontFamily: {
      heading: "'Plus Jakarta Sans', Inter, system-ui, -apple-system, sans-serif",
      body: 'Inter, system-ui, -apple-system, sans-serif',
      mono: "'JetBrains Mono', 'Fira Code', monospace",
      accent: 'Inter, system-ui, -apple-system, sans-serif',
    },
    fontWeight: {
      heading: {
        semiBold: 600,
        bold: 700,
        extraBold: 800,
        black: 900,
      },
      body: {
        regular: 400,
        medium: 500,
        semiBold: 600,
      },
      accent: {
        regular: 400,
        medium: 500,
      },
    },
  },

  // Spacing
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
  },

  // Border Radius
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '20px',
    round: '50%',
  },

  // Shadows (layered, more sophisticated — less garish than flat red)
  shadows: {
    sm: '0 2px 8px rgba(0, 0, 0, 0.4)',
    md: '0 4px 16px rgba(0, 0, 0, 0.5)',
    lg: '0 8px 32px -4px rgba(0, 0, 0, 0.6)',
    xl: '0 12px 48px -8px rgba(0, 0, 0, 0.7)',
    glow: '0 0 16px 2px rgba(220, 38, 38, 0.25)',
    neonGlow: '0 0 20px rgba(220, 38, 38, 0.5), 0 0 40px rgba(220, 38, 38, 0.2)',
    cardHover: '0 8px 32px -4px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(139, 92, 246, 0.1)',
    inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)',
  },

  // Gradients
  gradients: {
    primary: 'linear-gradient(135deg, #dc2626 0%, #991b1b 50%, #7f1d1d 100%)',
    primarySubtle: 'linear-gradient(135deg, rgba(220, 38, 38, 0.15) 0%, rgba(153, 27, 27, 0.05) 100%)',
    surface: 'linear-gradient(135deg, rgba(22, 29, 42, 0.8) 0%, rgba(13, 17, 23, 0.9) 100%)',
    divider: 'linear-gradient(90deg, transparent 0%, rgba(39, 48, 63, 0.4) 50%, transparent 100%)',
    bodyBg: 'radial-gradient(at 100% 100%, hsl(0, 50%, 8%) 0px, transparent 50%), radial-gradient(at 0% 0%, hsl(220, 30%, 6%) 0px, transparent 50%)',
  },

  // Animations
  animations: {
    // Durations
    fast: '0.15s',
    normal: '0.3s',
    slow: '0.5s',
    slower: '0.8s',

    // Easings
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
};

// Common MUI SX Styles
export const commonStyles = {
  // Card Styles — matches Gravemint's bg-white/5 border border-white/10 pattern
  card: {
    p: 3,
    bgcolor: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 3,
    transition: `all ${theme.animations.normal} ${theme.animations.easeInOut}`,
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.05)',
      borderColor: 'rgba(255,255,255,0.12)',
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows.cardHover,
    },
  },

  // Card without hover effects (for static containers)
  cardStatic: {
    p: 3,
    bgcolor: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 3,
  },

  // Glass Card (more transparent, for overlays/panels)
  glassCard: {
    p: 3,
    bgcolor: theme.colors.surface900 + '80',
    border: `1px solid ${theme.colors.borderSubtle}`,
    borderRadius: 3,
    backdropFilter: 'blur(20px)',
    transition: `all ${theme.animations.normal} ${theme.animations.easeInOut}`,
  },

  // Glass Card Strong (for sidebars, panels)
  glassCardStrong: {
    p: 3,
    bgcolor: theme.colors.bgGlassStrong,
    border: `1px solid ${theme.colors.borderSubtle}`,
    borderRadius: 3,
    backdropFilter: 'blur(24px)',
  },

  // Icon Box
  iconBox: {
    p: 1.5,
    borderRadius: 2,
    bgcolor: theme.colors.primaryAlpha15,
    color: theme.colors.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: `all ${theme.animations.normal} ${theme.animations.easeInOut}`,
  },

  // Button Primary — with neon glow on hover (matches Launchpad)
  buttonPrimary: {
    bgcolor: theme.colors.primary,
    color: theme.colors.textPrimary,
    fontWeight: 600,
    px: 3,
    py: 1.5,
    borderRadius: 2,
    transition: `all ${theme.animations.normal} ${theme.animations.easeInOut}`,
    '&:hover': {
      bgcolor: theme.colors.primaryDark,
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows.neonGlow,
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
  },

  // Button Outlined
  buttonOutlined: {
    borderColor: theme.colors.borderPrimary,
    color: theme.colors.primary,
    fontWeight: 600,
    px: 3,
    py: 1.5,
    borderRadius: 2,
    transition: `all ${theme.animations.normal} ${theme.animations.easeInOut}`,
    '&:hover': {
      borderColor: theme.colors.borderPrimaryHover,
      bgcolor: theme.colors.primaryAlpha10,
      transform: 'translateY(-2px)',
    },
  },

  // Button Ghost (subtle, for secondary actions)
  buttonGhost: {
    color: theme.colors.textSecondary,
    fontWeight: 500,
    px: 2,
    py: 1,
    borderRadius: 2,
    transition: `all ${theme.animations.normal} ${theme.animations.easeInOut}`,
    '&:hover': {
      bgcolor: 'rgba(255, 255, 255, 0.05)',
      color: theme.colors.textPrimary,
    },
  },

  // Input Field
  input: {
    '& .MuiOutlinedInput-root': {
      bgcolor: theme.colors.surface900,
      borderRadius: 2,
      transition: `all ${theme.animations.normal} ${theme.animations.easeInOut}`,
      '& fieldset': {
        borderColor: theme.colors.surface700,
      },
      '&:hover fieldset': {
        borderColor: theme.colors.surface600,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.colors.primary,
        boxShadow: `0 0 0 3px ${theme.colors.primaryAlpha10}`,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.colors.textMuted,
      '&.Mui-focused': {
        color: theme.colors.primary,
      },
    },
  },

  // Chip / Pill — brand accent variant
  chip: {
    bgcolor: theme.colors.primaryAlpha10,
    color: theme.colors.primaryLight,
    border: `1px solid ${theme.colors.primaryAlpha15}`,
    fontWeight: 600,
    fontSize: '0.8rem',
    transition: `all ${theme.animations.normal} ${theme.animations.easeInOut}`,
    '&:hover': {
      bgcolor: theme.colors.primaryAlpha15,
    },
  },

  // Pill variants
  pillSuccess: {
    bgcolor: 'rgba(16, 185, 129, 0.15)',
    color: '#34d399',
    border: '1px solid rgba(16, 185, 129, 0.2)',
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  pillWarning: {
    bgcolor: 'rgba(245, 158, 11, 0.15)',
    color: '#fbbf24',
    border: '1px solid rgba(245, 158, 11, 0.2)',
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  pillError: {
    bgcolor: 'rgba(239, 68, 68, 0.15)',
    color: '#f87171',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    fontWeight: 600,
    fontSize: '0.8rem',
  },

  // Tab
  tab: {
    borderBottom: `1px solid ${theme.colors.borderSubtle}`,
    '& .MuiTab-root': {
      color: theme.colors.textMuted,
      fontWeight: 600,
      textTransform: 'none',
      minHeight: 44,
      transition: `all ${theme.animations.normal} ${theme.animations.easeInOut}`,
      '&.Mui-selected': {
        color: theme.colors.textPrimary,
      },
      '&:hover': {
        color: theme.colors.textSecondary,
        bgcolor: 'rgba(255, 255, 255, 0.03)',
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: theme.colors.primary,
      height: 2,
      borderRadius: '2px 2px 0 0',
    },
  },

  // Section Heading (for page section titles)
  sectionHeading: {
    fontFamily: theme.typography.fontFamily.heading,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: theme.colors.textPrimary,
    mb: 1,
  },

  // Typography
  heading: {
    fontFamily: theme.typography.fontFamily.heading,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: theme.colors.textPrimary,
    mb: 2,
  },

  subheading: {
    fontWeight: 700,
    color: theme.colors.textPrimary,
    mb: 1.5,
  },

  body: {
    color: theme.colors.textSecondary,
    lineHeight: 1.7,
  },

  // Gradient text (use with WebkitBackgroundClip: 'text')
  gradientText: {
    background: theme.gradients.primary,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },

  // Accordion
  accordion: {
    bgcolor: theme.colors.surface900,
    border: `1px solid ${theme.colors.borderSubtle}`,
    borderRadius: '12px !important',
    mb: 1.5,
    '&:before': { display: 'none' },
    transition: `all ${theme.animations.normal} ${theme.animations.easeInOut}`,
    '&:hover': {
      borderColor: theme.colors.surface600,
    },
    '&.Mui-expanded': {
      borderColor: theme.colors.surface600,
      boxShadow: theme.shadows.sm,
    },
  },

  // Dialog
  dialog: {
    '& .MuiDialog-paper': {
      bgcolor: theme.colors.surface900,
      border: `1px solid ${theme.colors.surface700}60`,
      borderRadius: 3,
      backdropFilter: 'blur(24px)',
    },
  },

  // Divider (gradient — matches Graveyard)
  gradientDivider: {
    height: '1px',
    background: theme.gradients.divider,
    border: 'none',
    my: 3,
  },
};

// Framer Motion Variants
export const motionVariants = {
  // Fade In
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 },
  },

  // Fade In Up (Graveyard-style reveal)
  fadeInUp: {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
    transition: { duration: 0.4, ease: [0, 0, 0.2, 1] },
  },

  // Slide Up
  slideUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
  },

  // Slide Down
  slideDown: {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
  },

  // Scale In (for modals, popovers)
  scaleIn: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
  },

  // Scale (springy)
  scale: {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.9 },
    transition: { duration: 0.3, ease: [0.34, 1.56, 0.64, 1] },
  },

  // Stagger Children
  staggerContainer: {
    animate: {
      transition: {
        staggerChildren: 0.06,
      },
    },
  },

  // Stagger item (pair with staggerContainer)
  staggerItem: {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3, ease: [0, 0, 0.2, 1] },
  },

  // Card Hover
  cardHover: {
    rest: { scale: 1, y: 0 },
    hover: {
      scale: 1.02,
      y: -4,
      transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
    },
  },
};

export default theme;
